<template>
  <span>
    <slot name="button" v-bind="{ onclick: toggle }">
      <b-button variant="success" @click="toggle"><i class="fas fa-ban"></i> Block Country</b-button>
    </slot>

    <b-modal ref="modal-container" v-model="modalShow" size="lg" :static="true" hide-footer
      :title="title" @show="onShow" @hidden="onHidden">

      <b-form-group label="Country" label-for="country-code-input">
        <CountryCodeSelect id="country-code-input" v-model="countryCode" />
      </b-form-group>

      <div v-if="normalizedCode && exceptionsChecking" class="text-muted small">
        <i class="fas fa-spinner fa-spin"></i> Checking saved exceptions...
      </div>

      <b-alert v-if="showExceptionsPrompt" show variant="warning" class="mt-2 mb-0">
        Saved exceptions already exist for <b>{{ normalizedCode }}</b>.
        Keep them or clear and configure new ones?
        <div class="mt-2">
          <b-button size="sm" variant="success" class="mr-2" @click="exceptionsDecision = 'keep'">
            Keep existing
          </b-button>
          <b-button size="sm" variant="outline-danger" @click="exceptionsDecision = 'clear'">
            Clear all &amp; configure new
          </b-button>
        </div>
      </b-alert>

      <p v-if="exceptionsDecision === 'keep'" class="text-muted small mt-2 mb-0">
        Existing exceptions will be kept and shown in the <b>Exceptions</b> section.
      </p>
      <p v-else-if="exceptionsDecision === 'clear'" class="text-muted small mt-2 mb-0">
        All saved exceptions for <b>{{ normalizedCode }}</b> will be removed when you save.
        Configure new ones in the <b>Exceptions</b> section after blocking.
      </p>

      <p v-else class="text-muted small mt-3 mb-0">
        The country will be blocked for <b>all operators</b>.
        Configure operator-specific exceptions in the <b>Exceptions</b> section.
      </p>

      <div class="mt-3 text-right">
        <b-button variant="primary" class="mr-2" :disabled="!canSave || saving" @click="save">
          <i class="fas fa-save"></i> Save
        </b-button>
        <b-button variant="secondary" @click="modalShow = false">Close</b-button>
      </div>
    </b-modal>
  </span>
</template>

<script>
import CountryCodeSelect from "./CountryCodeSelect.vue";
import { findCountryByCode } from "./countryOptions";
import { fetchCountryExceptions, hasExceptionItems } from "./exceptionUtils";

export default {
  name: "BlockCountryForm",
  components: { CountryCodeSelect },
  props: {
    title: { type: String, default: 'Block country' },
    initialCode: { type: String, default: '' },
  },
  data() {
    return {
      modalShow: false,
      countryCode: '',
      saving: false,
      exceptionsChecking: false,
      hasExistingExceptions: false,
      exceptionsDecision: null,
    };
  },
  computed: {
    normalizedCode() {
      const c = (this.countryCode || '').trim().toUpperCase();
      return c === '' ? null : c;
    },
    showExceptionsPrompt() {
      return this.normalizedCode
        && this.hasExistingExceptions
        && !this.exceptionsChecking
        && !this.exceptionsDecision;
    },
    canSave() {
      if (!this.normalizedCode) return false;
      if (this.hasExistingExceptions && !this.exceptionsDecision) return false;
      return true;
    },
  },
  watch: {
    countryCode() {
      this.exceptionsDecision = null;
      this.checkExistingExceptions();
    },
  },
  methods: {
    toggle() {
      this.modalShow = !this.modalShow;
    },
    onHidden() {
      this.countryCode = '';
      this.hasExistingExceptions = false;
      this.exceptionsChecking = false;
      this.exceptionsDecision = null;
    },
    async onShow() {
      this.countryCode = this.initialCode || '';
      this.exceptionsDecision = null;
      await this.checkExistingExceptions();
    },
    getCountryName() {
      return findCountryByCode(this.normalizedCode)?.label || this.normalizedCode;
    },
    async checkExistingExceptions() {
      this.hasExistingExceptions = false;

      if (!this.normalizedCode) {
        return;
      }

      this.exceptionsChecking = true;
      try {
        const data = await fetchCountryExceptions(
          (config) => this.$http(config),
          this.normalizedCode,
        );
        this.hasExistingExceptions = hasExceptionItems(data);
      } catch (err) {
        this.$toastr.e('Failed to check saved exceptions');
      } finally {
        this.exceptionsChecking = false;
      }
    },
    async save() {
      if (!this.canSave) {
        if (this.hasExistingExceptions && !this.exceptionsDecision) {
          this.$toastr.w('Choose whether to keep or clear existing exceptions first');
        }
        return;
      }

      this.saving = true;
      try {
        await this.$http({
          url: '/api/v1/country-blocks/block',
          method: 'POST',
          data: {
            code: this.normalizedCode,
            blocked: true,
            name: this.getCountryName(),
            clearExceptions: this.exceptionsDecision === 'clear',
          },
          headers: { 'Content-Type': 'application/json' },
        });

        let message = 'Country blocked for all operators';
        if (this.exceptionsDecision === 'keep') {
          message = `Country blocked. Existing exceptions for ${this.normalizedCode} were kept.`;
        } else if (this.exceptionsDecision === 'clear') {
          message = `Country blocked. Exceptions for ${this.normalizedCode} were cleared.`;
        }
        this.$toastr.i(message);
        this.$emit('finish');
        this.modalShow = false;
      } catch (err) {
        const message = err.response?.data?.message ?? err.message;
        this.$toastr.e(typeof message === 'string' ? message : JSON.stringify(message));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
